/**
 * NewOrderModal — Feb 2026
 * ------------------------------------------------------------------
 * Simplified BRANCH order creation for admins with the `orders.create`
 * permission (also super admin). Mirrors `branch_place_order` but is
 * driven from the Admin Orders page — so ops can cut branch orders
 * without impersonating the branch account.
 *
 *   1. Pick a branch (search by name / username / customer #).
 *   2. Add items (search by SKU or name → click to add → set qty).
 *   3. Submit.
 *
 * On submit we POST to `/api/admin/orders` with
 *   { order_type: 'branch', branch_account_id: <branch.id>, ... }.
 * `admin_create_order` recognises those flags and stamps the order
 * exactly like a branch-placed one — same `order_type: "branch"` +
 * `branch_account_id` shape, so the order surfaces in the branch's
 * `/branch/orders` list and flows through the standard confirm →
 * FEFO → dispatch pipeline.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Search, X, Building2, Package } from 'lucide-react';
import { API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';

const priceOf = (p) => Number(p?.price ?? p?.base_price ?? 0) || 0;
// In-hand / available quantity. Server exposes both `available_qty`
// (stock_qty − allocated_qty, allocation-aware) and `stock_qty`.
// Prefer the allocation-aware figure so admins can't oversell rows
// already reserved by other pending orders. Falls back to `stock_qty`
// for older payload shapes.
const availableOf = (p) => {
  if (!p) return 0;
  const v = (p.available_qty !== undefined && p.available_qty !== null)
    ? p.available_qty
    : p.stock_qty;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};
const branchLabel = (b) => b?.name || b?.username || b?.email || '—';

const NewOrderModal = ({ branches, products, onClose, onCreated, authHeaders }) => {
  const { toast } = useToast();
  const [branchQuery, setBranchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [productQuery, setProductQuery] = useState('');
  // { [product_id]: qty }
  const [cart, setCart] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const branchList = Array.isArray(branches) ? branches : [];

  const filteredBranches = useMemo(() => {
    const q = branchQuery.trim().toLowerCase();
    if (!q) return branchList.slice(0, 30);
    return branchList.filter((b) => (
      (b.name || '').toLowerCase().includes(q)
      || (b.username || '').toLowerCase().includes(q)
      || (b.email || '').toLowerCase().includes(q)
      || (b.customer_numbers || []).some((c) => String(c).toLowerCase().includes(q))
    )).slice(0, 30);
  }, [branchQuery, branchList]);

  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products.slice(0, 30);
    return products.filter((p) => (
      (p.sku || '').toLowerCase().includes(q)
      || (p.name || '').toLowerCase().includes(q)
      || (p.item_code || '').toLowerCase().includes(q)
    )).slice(0, 30);
  }, [productQuery, products]);

  const productsById = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  const cartRows = Object.entries(cart)
    .map(([pid, qty]) => ({ product: productsById[pid], qty: Number(qty) }))
    .filter((r) => r.product && r.qty > 0);

  // Feb 2026 — block submit + surface an inline "Max: N" warning on
  // any line whose qty exceeds `available_qty` (allocation-aware
  // in-hand). Admins can still see the total-out-of-stock case
  // (`avail === 0`) as a full-line warning ("Out of stock").
  const cartRowsWithAvail = cartRows.map((r) => {
    const avail = availableOf(r.product);
    return { ...r, avail, over: r.qty > avail };
  });
  const hasOverCap = cartRowsWithAvail.some((r) => r.over);

  const subtotal = cartRows.reduce((acc, r) => acc + priceOf(r.product) * r.qty, 0);

  const addToCart = (product) => {
    setCart((prev) => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));
  };

  const setQty = (pid, qty) => {
    const n = Math.max(0, Math.floor(Number(qty) || 0));
    setCart((prev) => {
      if (n <= 0) {
        const next = { ...prev };
        delete next[pid];
        return next;
      }
      return { ...prev, [pid]: n };
    });
  };

  const removeFromCart = (pid) => setQty(pid, 0);

  const canSubmit = !!selectedBranch && cartRows.length > 0 && !submitting && !hasOverCap;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const items = cartRows.map((r) => ({
        id: r.product.id,
        name: r.product.name,
        sku: r.product.sku || r.product.item_code || '',
        size: r.product.size || '',
        size_type: r.product.size_type || 'size',
        price: priceOf(r.product),
        qty: r.qty,
        image: r.product.image || '',
        vat_applicable: !!r.product.vat_applicable,
        uom: r.product.uom || 'ctn',
      }));
      // Resolve branch defaults for `customer_*` fields so the server
      // has something to fall back to — but the backend will
      // overwrite these with the branch's canonical values anyway
      // (see `admin_create_order` branch-shape block).
      const branchEmails = Array.isArray(selectedBranch.emails)
        ? selectedBranch.emails.filter(Boolean)
        : (selectedBranch.email ? [selectedBranch.email] : []);
      const body = {
        customer_name: branchLabel(selectedBranch),
        customer_phone: '',
        customer_email: branchEmails[0] || `branch-${selectedBranch.id}@mrpco.local`,
        customer_address: '',
        customer_business_name: '',
        notes: '',
        items,
        // Branch orders default to "confirmed" server-side (mirrors
        // branch_place_order). Keep the client sending "draft" so the
        // server's branch-mode override converts it to "confirmed" —
        // making it a single code path we can audit.
        status: 'draft',
        order_type: 'branch',
        branch_account_id: selectedBranch.id,
      };
      const { data } = await axios.post(`${API}/admin/orders`, body, { headers: authHeaders });
      toast({
        title: 'Branch order created',
        description: `${data?.order_number || ''} · ${branchLabel(selectedBranch)} · ${items.length} item${items.length === 1 ? '' : 's'}`,
      });
      onCreated?.(data);
    } catch (e) {
      toast({
        title: 'Order create failed',
        description: e.response?.data?.detail || e.message || 'Try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="new-order-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-neutral-100 flex items-center justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">New Branch Order</div>
            <div className="font-display text-lg font-bold text-[#0F172A]">
              {selectedBranch ? branchLabel(selectedBranch) : 'Pick a branch to start'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 p-1"
            data-testid="new-order-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Step 1: pick a branch */}
          <section data-testid="new-order-branch-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Branch
              </div>
              {selectedBranch && (
                <button
                  type="button"
                  onClick={() => setSelectedBranch(null)}
                  className="text-[11px] font-semibold text-[#0078D4] hover:underline"
                  data-testid="new-order-change-branch-btn"
                >
                  Change
                </button>
              )}
            </div>
            {selectedBranch ? (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3"
                data-testid="new-order-selected-branch"
              >
                <Building2 className="w-5 h-5 text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-emerald-900">{branchLabel(selectedBranch)}</div>
                  <div className="text-[11px] text-emerald-800/80">
                    {selectedBranch.username && <span className="font-mono mr-2">@{selectedBranch.username}</span>}
                    {(selectedBranch.customer_numbers || []).length > 0 && (
                      <span className="mr-2">
                        cnum{selectedBranch.customer_numbers.length === 1 ? '' : 's'}:{' '}
                        <span className="font-mono">{selectedBranch.customer_numbers.join(', ')}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={branchQuery}
                    onChange={(e) => setBranchQuery(e.target.value)}
                    placeholder="Search by branch name, username, email or customer #"
                    className="w-full text-sm border border-neutral-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#0078D4]"
                    data-testid="new-order-branch-search"
                  />
                </div>
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="new-order-branch-results">
                  {filteredBranches.length === 0 && (
                    <div className="p-3 text-xs text-neutral-400">
                      {branchList.length === 0
                        ? 'No branches yet — create one in Admin → Branches.'
                        : 'No branches match — try a different search.'}
                    </div>
                  )}
                  {filteredBranches.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setSelectedBranch(b)}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2"
                      data-testid={`new-order-branch-option-${b.id}`}
                    >
                      <Building2 className="w-4 h-4 text-neutral-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A] truncate">{branchLabel(b)}</div>
                        <div className="text-[11px] text-neutral-500 truncate">
                          {b.username && <span className="font-mono mr-2">@{b.username}</span>}
                          {(b.customer_numbers || []).length > 0 && (
                            <span>
                              <span className="font-mono">{b.customer_numbers.join(', ')}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Step 2: items — only unlocked once a branch's picked */}
          {selectedBranch && (
            <section data-testid="new-order-items-section">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5 mb-2">
                <Package className="w-3.5 h-3.5" /> Items ({cartRows.length})
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Search by item code (SKU) or name"
                  className="w-full text-sm border border-neutral-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#0078D4]"
                  data-testid="new-order-item-search"
                />
              </div>
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="new-order-item-results">
                {filteredProducts.length === 0 && (
                  <div className="p-3 text-xs text-neutral-400">No matching products — check the item code or clear the search.</div>
                )}
                {filteredProducts.map((p) => {
                  const avail = availableOf(p);
                  const inStock = avail > 0;
                  return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addToCart(p)}
                    className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2"
                    data-testid={`new-order-item-option-${p.id}`}
                  >
                    <Plus className="w-4 h-4 text-[#0078D4] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A] truncate">
                        {p.name}
                        {p.sku && <span className="ml-2 font-mono text-[10px] text-neutral-500">{p.sku}</span>}
                        <span
                          className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                          data-testid={`new-order-item-inhand-${p.id}`}
                        >
                          In hand: {avail.toLocaleString()}
                        </span>
                      </div>
                      <div className="text-[11px] text-neutral-500 tabular-nums">
                        OMR {priceOf(p).toFixed(3)} / {p.uom || 'ctn'}
                        {(cart[p.id] || 0) > 0 && (
                          <span className="ml-2 text-emerald-700 font-semibold">In cart: {cart[p.id]}</span>
                        )}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>

              {/* Cart summary */}
              {cartRows.length > 0 && (
                <div className="mt-4 rounded-xl border border-neutral-200 divide-y divide-neutral-100" data-testid="new-order-cart">
                  {cartRowsWithAvail.map((r) => (
                    <div
                      key={r.product.id}
                      className={`p-3 flex items-center gap-3 ${r.over ? 'bg-red-50/40' : ''}`}
                      data-testid={`new-order-cart-row-${r.product.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A] truncate">
                          {r.product.name}
                          {r.product.sku && <span className="ml-2 font-mono text-[10px] text-neutral-500">{r.product.sku}</span>}
                          <span
                            className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${r.avail > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                            data-testid={`new-order-cart-inhand-${r.product.id}`}
                          >
                            In hand: {r.avail.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-[11px] text-neutral-500 tabular-nums">
                          OMR {priceOf(r.product).toFixed(3)} × {r.qty} = <span className="font-bold text-[#0F172A]">OMR {(priceOf(r.product) * r.qty).toFixed(3)}</span>
                        </div>
                        {r.over && (
                          <div
                            className="mt-1 text-[11px] font-semibold text-red-700 flex items-center gap-1"
                            data-testid={`new-order-cart-overcap-${r.product.id}`}
                          >
                            <span aria-hidden>⚠</span>
                            {r.avail > 0
                              ? `Exceeds in-hand stock — max available: ${r.avail.toLocaleString()}`
                              : 'Out of stock — nothing available to allocate'}
                          </div>
                        )}
                      </div>
                      <input
                        type="number"
                        min={1}
                        value={r.qty}
                        onChange={(e) => setQty(r.product.id, e.target.value)}
                        className={`w-16 text-sm text-center border rounded-lg px-2 py-1 focus:outline-none ${r.over ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-neutral-200 focus:border-[#0078D4]'}`}
                        data-testid={`new-order-cart-qty-${r.product.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeFromCart(r.product.id)}
                        className="text-neutral-400 hover:text-red-600 p-1"
                        data-testid={`new-order-cart-remove-${r.product.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="p-3 bg-neutral-50 flex items-center justify-between tabular-nums">
                    <span className="text-xs uppercase tracking-wider font-bold text-neutral-500">Subtotal</span>
                    <span className="text-base font-extrabold text-[#0F172A]" data-testid="new-order-subtotal">OMR {subtotal.toFixed(3)}</span>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="p-4 border-t border-neutral-100 sticky bottom-0 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
            data-testid="new-order-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="text-xs font-bold text-white px-4 py-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="new-order-submit-btn"
          >
            {submitting
              ? 'Creating…'
              : hasOverCap
                ? 'Fix stock warnings to submit'
                : cartRows.length > 0
                  ? `Submit · ${cartRows.length} item${cartRows.length === 1 ? '' : 's'}`
                  : 'Add items to submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrderModal;
